// ─── Watch Form Defaults & Summary ──────────────────────────────────────────

export type WatchIntent =
  | "good_time_to_buy"
  | "good_time_to_sell"
  | "iv_rank_threshold"
  | "price_target"
  | "volatility_spike";

export interface IntentParamField {
  key: string;
  label: string;
  type: "number" | "select";
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  hint?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

export interface WatchDefaults {
  policy: {
    max_duration_days: number;
    evaluation_interval_minutes: number;
    max_alerts_per_watch: number;
    cooldown_minutes: number;
    market_hours_only: boolean;
  };
  alertMode: "one_and_done" | "keep_watching";
  intentParams: Record<string, unknown>;
  requiresOptionContract: boolean;
  sensitivityPresets: { label: string; value: number; desc: string }[] | null;
  intentParamFields: IntentParamField[];
  intentLabel: string;
  intentDescription: string;
}

// ─── Sensitivity presets shared by buy/sell intents ─────────────────────────

const SENSITIVITY_PRESETS: WatchDefaults["sensitivityPresets"] = [
  { label: "Conservative", value: 0.3, desc: "Fewer alerts, higher confidence signals only" },
  { label: "Balanced", value: 0.5, desc: "Default balance of signal frequency and quality" },
  { label: "Aggressive", value: 0.7, desc: "More alerts, catches weaker signals too" },
];

// ─── Intent metadata ────────────────────────────────────────────────────────

const INTENT_META: Record<
  WatchIntent,
  { label: string; description: string }
> = {
  good_time_to_buy: {
    label: "Buy Signal",
    description:
      "Find optimal entry points using market activity, volatility, and momentum signals",
  },
  good_time_to_sell: {
    label: "Sell Signal",
    description:
      "Find optimal exit points using the same signal analysis",
  },
  iv_rank_threshold: {
    label: "IV Alert",
    description:
      "Get notified when implied volatility rank crosses your threshold",
  },
  price_target: {
    label: "Price Target",
    description:
      "Alert when the underlying price hits your target level",
  },
  volatility_spike: {
    label: "Volatility Spike",
    description:
      "Alert when contract volatility spikes beyond the configured regime",
  },
};

// ─── getDefaultsByIntent ────────────────────────────────────────────────────

export function getDefaultsByIntent(intent: string): WatchDefaults {
  const meta = INTENT_META[intent as WatchIntent] ?? {
    label: intent,
    description: "",
  };

  switch (intent as WatchIntent) {
    // ── Buy / Sell share the same policy shape ─────────────────────────
    case "good_time_to_buy":
    case "good_time_to_sell":
      return {
        policy: {
          max_duration_days: 5,
          evaluation_interval_minutes: 15,
          max_alerts_per_watch: 3,
          cooldown_minutes: 120,
          market_hours_only: true,
        },
        alertMode: "one_and_done",
        intentParams: { sensitivity: 0.5 },
        requiresOptionContract: true,
        sensitivityPresets: SENSITIVITY_PRESETS,
        intentParamFields: [],
        intentLabel: meta.label,
        intentDescription: meta.description,
      };

    // ── IV Rank ────────────────────────────────────────────────────────
    case "iv_rank_threshold":
      return {
        policy: {
          max_duration_days: 14,
          evaluation_interval_minutes: 60,
          max_alerts_per_watch: 3,
          cooldown_minutes: 240,
          market_hours_only: true,
        },
        alertMode: "keep_watching",
        intentParams: { threshold: 80 },
        requiresOptionContract: true,
        sensitivityPresets: null,
        intentParamFields: [
          {
            key: "threshold",
            label: "IV Rank Threshold",
            type: "number",
            min: 0,
            max: 100,
            step: 1,
            unit: "%",
            hint: "Alert when IV rank exceeds this percentile (0-100)",
            required: true,
          },
        ],
        intentLabel: meta.label,
        intentDescription: meta.description,
      };

    // ── Price Target ──────────────────────────────────────────────────
    case "price_target":
      return {
        policy: {
          max_duration_days: 14,
          evaluation_interval_minutes: 15,
          max_alerts_per_watch: 1,
          cooldown_minutes: 0,
          market_hours_only: true,
        },
        alertMode: "one_and_done",
        intentParams: { target_price: 0, direction: "above" },
        requiresOptionContract: false,
        sensitivityPresets: null,
        intentParamFields: [
          {
            key: "target_price",
            label: "Target Price",
            type: "number",
            min: 0.01,
            step: 0.01,
            unit: "$",
            hint: "The price level to watch for",
            required: true,
          },
          {
            key: "direction",
            label: "Direction",
            type: "select",
            required: true,
            options: [
              { value: "above", label: "Rises above" },
              { value: "below", label: "Falls below" },
            ],
          },
        ],
        intentLabel: meta.label,
        intentDescription: meta.description,
      };

    case "volatility_spike":
      return {
        policy: {
          max_duration_days: 14,
          evaluation_interval_minutes: 60,
          max_alerts_per_watch: 3,
          cooldown_minutes: 240,
          market_hours_only: true,
        },
        alertMode: "keep_watching",
        intentParams: { sensitivity: "medium" },
        requiresOptionContract: true,
        sensitivityPresets: null,
        intentParamFields: [],
        intentLabel: meta.label,
        intentDescription: meta.description,
      };

    // ── Fallback (same shape as buy) ──────────────────────────────────
    default:
      return {
        policy: {
          max_duration_days: 5,
          evaluation_interval_minutes: 15,
          max_alerts_per_watch: 3,
          cooldown_minutes: 120,
          market_hours_only: true,
        },
        alertMode: "one_and_done",
        intentParams: {},
        requiresOptionContract: false,
        sensitivityPresets: null,
        intentParamFields: [],
        intentLabel: meta.label,
        intentDescription: meta.description,
      };
  }
}

// ─── Duration helpers ───────────────────────────────────────────────────────

function fmtDays(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

function formatShortDate(value: Date): string {
  return value.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function describeMonitoringWindow(maxDays: number, contractExpiry?: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (contractExpiry) {
    const parts = contractExpiry.split("-");
    if (parts.length === 3) {
      const expiry = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      const dte = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (dte > 0 && dte <= maxDays) {
        return `through ${formatShortDate(expiry)} expiry (${dte} DTE)`;
      }
    }
  }

  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + maxDays);
  return `through ${formatShortDate(endDate)} (${fmtDays(maxDays)})`;
}

// ─── computeSummary ─────────────────────────────────────────────────────────

export function computeSummary(state: {
  ticker: string;
  intent: string;
  contractExpiry?: string;
  contractStrike?: string;
  contractType?: string;
  policy: WatchDefaults["policy"];
  intentParams: Record<string, unknown>;
  alertMode?: "one_and_done" | "keep_watching";
}): string {
  const {
    ticker,
    intent,
    contractExpiry,
    contractStrike,
    contractType,
    policy,
    intentParams,
    alertMode,
  } = state;

  if (!ticker) return "";

  const sym = ticker.toUpperCase();
  const monitoringWindow = describeMonitoringWindow(policy.max_duration_days, contractExpiry);
  const scheduleNote = policy.market_hours_only
    ? `Monitor during market hours ${monitoringWindow}.`
    : `Monitor 24/7 ${monitoringWindow}.`;
  const alertSuffix =
    alertMode === "keep_watching"
      ? " Continues monitoring for repeat signals."
      : " Single alert, then watch completes.";

  // ── Buy / Sell ──────────────────────────────────────────────────────
  if (intent === "good_time_to_buy" || intent === "good_time_to_sell") {
    const action = intent === "good_time_to_buy" ? "buying" : "selling";

    // Build contract descriptor if present
    let contractDesc = "";
    if (contractStrike && contractType) {
      const typeLabel =
        contractType.charAt(0).toUpperCase() + contractType.slice(1);
      contractDesc = contractExpiry
        ? ` $${contractStrike} ${typeLabel} (${contractExpiry})`
        : ` $${contractStrike} ${typeLabel}`;
    }

    return (
      `Watch ${sym}${contractDesc} for ${action} opportunities. ` +
      `${scheduleNote}${alertSuffix}`
    );
  }

  // ── IV Rank ─────────────────────────────────────────────────────────
  if (intent === "iv_rank_threshold") {
    const threshold = intentParams.threshold ?? 80;
    return (
      `Watch ${sym}: alert when IV rank exceeds ${threshold}th percentile. ` +
      `${scheduleNote}${alertSuffix}`
    );
  }

  // ── Price Target ────────────────────────────────────────────────────
  if (intent === "price_target") {
    const price =
      intentParams.target_price != null
        ? Number(intentParams.target_price).toFixed(2)
        : "—";
    const dir = intentParams.direction === "below" ? "falls below" : "rises above";
    return (
      `Watch ${sym}: alert when price ${dir} $${price}. ` +
      `${scheduleNote}${alertSuffix}`
    );
  }

  if (intent === "volatility_spike") {
    const sensitivity = intentParams.sensitivity || "medium";
    return (
      `Watch ${sym}: alert on a ${sensitivity} sensitivity volatility spike. ` +
      `${scheduleNote}${alertSuffix}`
    );
  }

  // ── Generic fallback ───────────────────────────────────────────────
  return (
    `Watch ${sym}. ${scheduleNote}${alertSuffix}`
  );
}
